const angular = require('angular'), { isEmpty } = require('lodash');

function inputContainerDirective() {
    return {
        restrict: 'E',
        controller: [
            '$scope', '$element', function ($scope, $element) {
                this.element = $element;
                this.messagePanel = $element.find('[ng-messages]').first();
                this.setFocused = function (isFocused) {
                    $element.toggleClass('is-focused', !!isFocused);
                };
                this.setHasValue = function (hasValue) {
                    $element.toggleClass('has-value', !!hasValue);
                };
                this.setInvalid = function (isInvalid) {
                    $element.toggleClass('is-invalid', !!isInvalid);

                    // Set aria attribute to input so error message is read out on focus.
                    if (this.messagePanel.length) {
                        if (isInvalid) {
                            let msgId = this.messagePanel.attr('id');
                            if (!msgId) {
                                msgId = `${this.input.attr('id')}-messages`;
                                this.messagePanel.attr('id', msgId);
                            }

                            this.input.attr('aria-describedby', msgId);
                        } else {
                            this.input.removeAttr('aria-describedby');
                        }
                    }
                };
                $scope.$watch(() => this.label && this.input, (hasLabelAndInput) => {
                    if (hasLabelAndInput && !this.label.attr('for')) {
                        this.label.attr('for', this.input.attr('id'));
                    }
                });
            },
        ],
    };
}

function fakeNgModel() {
    return {
        $fake: true,
        $setViewValue(value) {
            this.$viewValue = value;
            this.$render(value);
            this.$viewChangeListeners.forEach((cb) => { cb(); });
        },
        $isEmpty(value) {
            return (`${value}`).length === 0;
        },
        $parsers: [],
        $formatters: [],
        $viewChangeListeners: [],
        $render: angular.noop,
    };
}

function inputDirective(guid) {
    return {
        restrict: 'E',
        require: ['^?wowInputContainer', '?ngModel', '^?form'],
        link(scope, element, attr, ctrls) {

            const containerCtrl = ctrls[0];
            const ngModelCtrl = ctrls[1] || fakeNgModel();
            const ngForm = ctrls[2] || { $submitted: true };
            const isReadonly = angular.isDefined(attr.readonly);
            if (!containerCtrl) {
                return;
            }
            if (containerCtrl.input) {
                throw new Error('<wow-input-container> can only have *one* <input> child element!');
            }
            containerCtrl.input = element;

            element.addClass('wowInput');
            if (!element.attr('id')) {
                element.attr('id', `input_${guid()}`);
            }

            function ngModelPipelineCheckValue(arg) {
                containerCtrl.setHasValue(!ngModelCtrl.$isEmpty(arg));
                return arg;
            }

            function inputCheckValue() {
                // An input's value counts if its length > 0,
                // or if the input's validity state says it has bad input (eg string in a number input)
                containerCtrl.setHasValue(element.val().length > 0 || (element[0].validity || {}).badInput);
            }

            const isErrorGetter = function () {
                // check if field is invalid when submitted or validated asynchronusly
                return ngModelCtrl.$invalid
                    && (ngForm.$submitted || ngModelCtrl.$dirty && !isEmpty(ngModelCtrl.$asyncValidators));
            };

            scope.$watch(isErrorGetter, (isInvalid) => {
                containerCtrl.setInvalid(isInvalid);
                element.attr('aria-invalid', isInvalid);
            });

            ngModelCtrl.$parsers.push(ngModelPipelineCheckValue);
            ngModelCtrl.$formatters.push(ngModelPipelineCheckValue);

            element.on('input', inputCheckValue);

            if (!isReadonly) {
                element
                    .on('focus', () => {
                        containerCtrl.setFocused(true);
                    })
                    .on('blur', () => {
                        containerCtrl.setFocused(false);
                        inputCheckValue();
                    });
            }

            scope.$on('$destroy', () => {
                containerCtrl.setFocused(false);
                containerCtrl.setHasValue(false);
                containerCtrl.input = null;
            });
        },
    };
}

function labelDirective() {
    return {
        restrict: 'E',
        require: '^?wowInputContainer',
        link(scope, element, attr, containerCtrl) {
            if (!containerCtrl) {
                return;
            }

            containerCtrl.label = element;
            element.addClass('wowLabel');

            scope.$on('$destroy', () => {
                containerCtrl.label = null;
            });
        },
    };
}

function placeholderDirective() {
    return {
        restrict: 'A',
        require: '^^?wowInputContainer',
        link(scope, element, attr, inputContainer) {
            if (!inputContainer || attr.keepPlaceholder) {
                return;
            }
            const placeholderText = attr.placeholder;
            element.removeAttr('placeholder');

            inputContainer.element.find('input').after(`<div class="wowPlaceholder">${placeholderText}</div>`);
        },
    };
}

function getKeyCode(evt) {
    const key = evt.key;
    let keyCode = evt.keyCode;
    if (key && !keyCode) {
        keyCode = key.charCodeAt(0);
    }
    return keyCode;
}

function isEnterKey(keyCode) {
    return keyCode && keyCode === 13;
}

function isBackspace(keyCode) {
    return keyCode && keyCode === 8;
}

function matchesPattern(regex, value) {
    return regex.test(value);
}

function restrictAlphaDirective() {
    return {
        restrict: 'A',
        link(scope, element) {
            function isNumeric(keyCode) {
                return keyCode && keyCode >= 48 && keyCode <= 57;
            }
            element.bind('keypress', (event) => {
                const keyCode = getKeyCode(event);
                if (!isBackspace(keyCode) && !isNumeric(keyCode) && !isEnterKey(keyCode)) {
                    event.preventDefault();
                }
            });
            element.bind('keyup', () => {
                if (!matchesPattern(/^\d+$/, element.val())) {
                    element.val('');
                }
            });
        },
    };
}

function maxLengthDirective() {
    return {
        restrict: 'A',
        link(scope, element, attrs) {
            function hasReachedMaxLength(value, maxLength) {
                return value && value.length && value.length === maxLength;
            }
            function hasSelectedText(selectionIndexStart, selectionIndexEnd, maxLength) {
                return selectionIndexStart !== maxLength && selectionIndexStart !== selectionIndexEnd;
            }
            function hasPassedMaxLength(value, maxLength) {
                return value && value.length && value.length > maxLength;
            }
            element.bind('keypress', (event) => {
                const maxLength = scope.$eval(attrs.olyMaxLength);
                const value = element.val();
                const keyCode = getKeyCode(event);
                if (!isBackspace(keyCode)
                && hasReachedMaxLength(value, maxLength)
                && !hasSelectedText(event.target.selectionStart, event.target.selectionEnd, maxLength)) {
                    event.preventDefault();
                }
            });
            element.bind('keyup', () => {
                const maxLength = scope.$eval(attrs.olyMaxLength);
                const value = element.val();
                if (hasPassedMaxLength(value, maxLength)) {
                    element.val('');
                }
            });
        },
    };
}

module.exports = {
    inputContainer: inputContainerDirective,
    input: inputDirective,
    label: labelDirective,
    placeholder: placeholderDirective,
    restrictAlpha: restrictAlphaDirective,
    maxLength: maxLengthDirective,
};



// WEBPACK FOOTER //
// ./src/olycommon/input/input.directive.js